/**
 * @file useWmsDashboard.ts
 * @description Данные дашборда «Склад сегодня» (phase19.24 этап 1, шаг 3).
 *
 * Один запрос на экран (`GET /admin/wms/dashboard`) вместо пяти параллельных
 * выборок прежней главной: на складском Wi-Fi каждый лишний запрос — лишний
 * шанс показать пустой экран, а счётчики всё равно считает сервер.
 *
 * Выбранный склад живёт в URL (`?wh=`) — теми же именами, что фильтр остатков:
 * ссылку на сводку конкретного склада шлют коллеге, а обновление страницы не
 * сбрасывает отбор.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { DASHBOARD_TEXT } from "./dashboard-texts";
import {
  wmsDashboardApi,
  type WmsDashboardDto,
} from "../../../lib/api-wms-dashboard";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Имя параметра склада в адресе — общее с экраном «Остатки». */
const WAREHOUSE_PARAM = "wh";

export interface WmsDashboardState {
  data: WmsDashboardDto | null;
  loading: boolean;
  error: string | null;
  /** Пустая строка — сводка по всем складам магазина. */
  warehouseId: string;
  setWarehouseId: (id: string) => void;
  reload: () => void;
  /** Момент последней успешной загрузки — подпись «Показаны данные на 14:32». */
  loadedAt: Date | null;
  /** Браузер сообщает, что сети нет (phase19.31, О4 — текст «Нет сети»). */
  offline: boolean;
}

export function useWmsDashboard(): WmsDashboardState {
  const [searchParams, setSearchParams] = useSearchParams();
  const warehouseId = searchParams.get(WAREHOUSE_PARAM) ?? "";
  const [data, setData] = useState<WmsDashboardDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Счётчик ручных перезагрузок — им же перезапускаем эффект по «Повторить». */
  const [attempt, setAttempt] = useState(0);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const setWarehouseId = useCallback(
    (id: string) => {
      const params = new URLSearchParams(searchParams);
      if (id) params.set(WAREHOUSE_PARAM, id);
      else params.delete(WAREHOUSE_PARAM);
      // replace: переключение склада не должно копиться в истории — «Назад»
      // обязано уводить со страницы, а не отматывать перебор складов.
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    wmsDashboardApi
      .get({ warehouseId: warehouseId || undefined })
      .then((next) => {
        if (cancelled) return;
        setData(next);
        setLoadedAt(new Date());
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Данные прошлой загрузки оставляем: сводка на экране лучше пустого
        // места, а полоса ошибки скажет, что цифры устарели.
        setError(wmsErrorText(err, DASHBOARD_TEXT.loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId, attempt]);

  return {
    data,
    loading,
    error,
    warehouseId,
    setWarehouseId,
    reload: () => setAttempt((value) => value + 1),
    loadedAt,
    offline,
  };
}
